import {ReportField} from '../../../Models/ReportField';
import {Snippet} from '../../../../../../Models/Snippet';
import {formatAverageDistance, formatDistance} from '../../../../../Support/Formatting/formatDistance';
import {Distance} from '../../../../../../Domain/Distance';
import {Power} from '../../../../../../Domain/Power';
import {formatAveragePower} from '../../../../../Support/Formatting/formatPower';
import {formatSplit} from '../../../../../Support/Formatting/formatSplit';
import {Speed} from '../../../../../../Domain/Speed';
import {Duration} from '../../../../../../Domain/Duration';
import {formatAveragePeakForce} from '../../../../../Support/Formatting/formatPeakForce';
import {Force} from '../../../../../../Domain/Force';
import {formatAverageDriveTime} from '../../../../../Support/Formatting/formatDriveTime';
import {formatAverageRecoverTime} from '../../../../../Support/Formatting/formatRecoverTime';
import {formatAverageEnergy} from '../../../../../Support/Formatting/formatEnergy';
import {Energy} from '../../../../../../Domain/Energy';
import {formatLinearProgression} from '../../../../../Support/Formatting/formatLinearRegression';
import {Fraction} from '../../../../../../Domain/Fraction';
import {formatAverageRelativePeakForcePosition} from '../../../../../Support/Formatting/formatRelativePeakForcePosition';
import { formatAverageRSquared } from '../../../../../Support/Formatting/formatRSquared';

export interface ReportFieldConclusion {
    formatted: string;
    unit?: string;
    sign: string;
}

export function calculateConclusions(field: ReportField, snippets: Snippet[]): ReportFieldConclusion[] {
    const sortedSnippets = [...snippets].sort(
        (a, b) => a.publicWorkout.executedAt.getTime() - b.publicWorkout.executedAt.getTime(),
    );
    switch (field) {
        case ReportField.ElapsedTime:
            return [];
        case ReportField.TraveledDistance:
            return [
                {
                    ...formatAverageDistance(
                        Distance.average(...sortedSnippets.map((snippet) => snippet.traveledDistance)),
                    ),
                    sign: 'Ø',
                },
                {
                    ...formatDistance(Distance.sum(...sortedSnippets.map((snippet) => snippet.traveledDistance))),
                    sign: '∑',
                },
            ];
        case ReportField.AveragePower:
            const powerConclusion = [
                {
                    ...formatAveragePower(
                        Power.average(
                            ...snippets
                                .map((snippet) => snippet.averagePower)
                                .filter((power): power is Power => power !== null),
                        ),
                    ),
                    sign: 'Ø',
                },
            ];
            if (sortedSnippets.length > 1) {
                powerConclusion.push({
                    ...formatLinearProgression(
                        Fraction.fromValues(
                            snippets[sortedSnippets.length - 1].averagePower?.watts ?? 0,
                            snippets[0].averagePower?.watts ?? 0,
                        ),
                    ),
                });
            }
            return powerConclusion;
        case ReportField.AverageSplit:
            const splitConclusion = [
                {
                    ...formatSplit(
                        Speed.average(
                            ...snippets
                                .map((snippet) => snippet.averageSplit)
                                .filter((split): split is Duration => split !== null)
                                .map((split) => Speed.fromDurationPer500Meters(split))
                                .filter((speed): speed is Speed => speed !== null),
                        ),
                    ),
                    sign: 'Ø',
                },
            ];
            if (sortedSnippets.length > 1) {
                splitConclusion.push({
                    ...formatLinearProgression(
                        Fraction.fromValues(
                            snippets[sortedSnippets.length - 1].averageSplit?.seconds ?? 0,
                            snippets[0].averageSplit?.seconds ?? 0,
                        ),
                    ),
                });
            }
            return splitConclusion;
        case ReportField.AveragePeakForce:
            const peakForceConclusion = [
                {
                    ...formatAveragePeakForce(
                        Force.average(
                            ...snippets
                                .map((snippet) => snippet.averagePeakForce)
                                .filter((force): force is Force => force !== null),
                        ),
                    ),
                    sign: 'Ø',
                },
            ];
            if (sortedSnippets.length > 1) {
                peakForceConclusion.push({
                    ...formatLinearProgression(
                        Fraction.fromValues(
                            snippets[sortedSnippets.length - 1].averagePeakForce?.newton ?? 0,
                            snippets[0].averagePeakForce?.newton ?? 0,
                        ),
                    ),
                });
            }
            return peakForceConclusion;
        case ReportField.AverageDriveTime:
            const driveTimeConclusion = [
                {
                    ...formatAverageDriveTime(
                        Duration.average(
                            ...snippets
                                .map((snippet) => snippet.averageDriveTime)
                                .filter((driveTime): driveTime is Duration => driveTime !== null),
                        ),
                    ),
                    sign: 'Ø',
                },
            ];
            if (sortedSnippets.length > 1) {
                driveTimeConclusion.push({
                    ...formatLinearProgression(
                        Fraction.fromValues(
                            snippets[sortedSnippets.length - 1].averageDriveTime?.seconds ?? 0,
                            snippets[0].averageDriveTime?.seconds ?? 0,
                        ),
                    ),
                });
            }
            return driveTimeConclusion;
        case ReportField.AverageRecoverTime:
            const recoverTimeConclusion = [
                {
                    ...formatAverageRecoverTime(
                        Duration.average(
                            ...snippets
                                .map((snippet) => snippet.averageRecoverTime)
                                .filter((recoverTime): recoverTime is Duration => recoverTime !== null),
                        ),
                    ),
                    sign: 'Ø',
                },
            ];
            if (sortedSnippets.length > 1) {
                recoverTimeConclusion.push({
                    ...formatLinearProgression(
                        Fraction.fromValues(
                            snippets[sortedSnippets.length - 1].averageRecoverTime?.seconds ?? 0,
                            snippets[0].averageRecoverTime?.seconds ?? 0,
                        ),
                    ),
                });
            }
            return recoverTimeConclusion;
        case ReportField.AverageEnergyPerStroke:
            const energyPerStrokeConclusion = [
                {
                    ...formatAverageEnergy(
                        Energy.average(
                            ...snippets
                                .map((snippet) => snippet.averageEnergyPerStroke)
                                .filter((energy): energy is Energy => energy !== null),
                        ),
                    ),
                    sign: 'Ø',
                },
            ];
            if (sortedSnippets.length > 1) {
                energyPerStrokeConclusion.push({
                    ...formatLinearProgression(
                        Fraction.fromValues(
                            snippets[sortedSnippets.length - 1].averageEnergyPerStroke?.joules ?? 0,
                            snippets[0].averageEnergyPerStroke?.joules ?? 0,
                        ),
                    ),
                });
            }
            return energyPerStrokeConclusion;
        case ReportField.AverageStrokeLength:
            const strokeLengthConclusion = [
                {
                    ...formatAverageDistance(
                        Distance.average(
                            ...snippets
                                .map((snippet) => snippet.averageStrokeLength)
                                .filter((distance): distance is Distance => distance !== null),
                        ),
                    ),
                    sign: 'Ø',
                },
            ];
            if (sortedSnippets.length > 1) {
                strokeLengthConclusion.push({
                    ...formatLinearProgression(
                        Fraction.fromValues(
                            snippets[sortedSnippets.length - 1].averageStrokeLength?.meters ?? 0,
                            snippets[0].averageStrokeLength?.meters ?? 0,
                        ),
                    ),
                });
            }
            return strokeLengthConclusion;
        
        case ReportField.AverageRelativePeakForcePosition:
            const relativePeakForceConclusion = [
                {
                    ...formatAverageRelativePeakForcePosition(
                        Fraction.average(
                            ...snippets
                                .map((snippet) => snippet.averageRelativePeakForcePosition)
                                .filter((fraction): fraction is Fraction => fraction !== null),
                        ),
                    ),
                    sign: 'Ø',
                },
            ];
            if (sortedSnippets.length > 1) {
                relativePeakForceConclusion.push({
                    ...formatLinearProgression(
                        Fraction.fromValues(
                            snippets[sortedSnippets.length - 1].averageRelativePeakForcePosition?.value ?? 0,
                            snippets[0].averageRelativePeakForcePosition?.value ?? 0,
                        ),
                    ),
                });
            }
            return relativePeakForceConclusion;

        case ReportField.AverageRSquaredTotalStroke:
            const rSquaredTotalStrokeConclusion = [
                {
                    ...formatAverageRSquared(
                        Fraction.average(
                            ...snippets
                                .map((snippet) => snippet.rSquaredTotalStroke)
                                .filter((fraction): fraction is Fraction => fraction !== null),
                        ).value,
                    ),
                    sign: 'Ø',
                },
            ];
            if (sortedSnippets.length > 1) {
                    rSquaredTotalStrokeConclusion.push({
                    ...formatLinearProgression(
                        Fraction.fromValues(
                            snippets[sortedSnippets.length - 1].rSquaredTotalStroke?.value ?? 0,
                            snippets[0].rSquaredTotalStroke?.value ?? 0,
                        ),
                    ),
                });
            }
            return rSquaredTotalStrokeConclusion;

        case ReportField.AverageRSquaredPrePeak:
            const rSquaredPrePeakConclusion = [
                {
                    ...formatAverageRSquared(
                        Fraction.average(
                            ...snippets
                                .map((snippet) => snippet.rSquaredPrePeak)
                                .filter((fraction): fraction is Fraction => fraction !== null),
                        ).value,
                    ),
                    sign: 'Ø',
                },
            ];
            if (sortedSnippets.length > 1) {
                    rSquaredPrePeakConclusion.push({
                    ...formatLinearProgression(
                        Fraction.fromValues(
                            snippets[sortedSnippets.length - 1].rSquaredPrePeak?.value ?? 0,
                            snippets[0].rSquaredPrePeak?.value ?? 0,
                        ),
                    ),
                });
            }
            return rSquaredPrePeakConclusion;


        case ReportField.AverageRSquaredPostPeak:
            const rSquaredPostPeakConclusion = [
                {
                    ...formatAverageRSquared(
                        Fraction.average(
                            ...snippets
                                .map((snippet) => snippet.rSquaredPostPeak)
                                .filter((fraction): fraction is Fraction => fraction !== null),
                        ).value,
                    ),
                    sign: 'Ø',
                },
            ];
            if (sortedSnippets.length > 1) {
                    rSquaredPostPeakConclusion.push({
                    ...formatLinearProgression(
                        Fraction.fromValues(
                            snippets[sortedSnippets.length - 1].rSquaredPostPeak?.value ?? 0,
                            snippets[0].rSquaredPostPeak?.value ?? 0,
                        ),
                    ),
                });
            }
            return rSquaredPostPeakConclusion;


        case ReportField.ExecutedAt:
            return [];
    }
}
